import type { LeaderboardOutcome } from './localLeaderboard';
import { calculateLeaderboardScore } from './localLeaderboard';

export const TRAINING_PROGRESS_KEY = 'compliance_command_training_progress';
const STORAGE_KEY = 'compliance_command_player_analytics';
const MAX_RUN_HISTORY = 50;

export type RunMode = 'sandbox' | 'training' | 'mission';
export type CompletionGrade = 'A' | 'B' | 'C' | 'D' | 'F';

export interface RunCompletionRecord {
  id: string;
  date: string;
  scenarioId: string | null;
  scenarioTitle: string;
  mode: RunMode;
  outcome: LeaderboardOutcome;
  compliance: number;
  budget: number;
  threat: number;
  day: number;
  tickNumber: number;
  score: number;
  milestonesCompleted: number;
  milestonesTotal: number;
  objectivesCompleted: number;
  objectivesTotal: number;
  quizAttempted: boolean;
  quizPassed: boolean | null;
  rank?: number;
}

export interface ScenarioAggregate {
  scenarioId: string;
  scenarioTitle: string;
  mode: RunMode;
  attempts: number;
  wins: number;
  losses: number;
  bestScore: number;
  lastPlayed?: string;
  firstWin?: string;
}

export interface CareerSummary {
  totalRuns: number;
  totalWins: number;
  totalLosses: number;
  sandboxRuns: number;
  trainingRuns: number;
  missionRuns: number;
  trainingProgress: number;
  trainingTotal: number;
  uniqueScenariosPlayed: number;
  uniqueScenariosWon: number;
  bestOverallScore: number;
  avgScore: number;
  quizAttempts: number;
  quizPasses: number;
  winRate: number;
  academyCompletionRate: number;
  lastPlayed?: string;
}

export interface CompletionReview {
  grade: CompletionGrade;
  headline: string;
  highlights: string[];
  improvements: string[];
}

interface PlayerAnalyticsStore {
  version: 1;
  runs: RunCompletionRecord[];
  scenarioStats: Record<string, ScenarioAggregate>;
}

export interface RecordRunInput {
  scenarioId?: string | null;
  scenarioTitle: string;
  outcome: LeaderboardOutcome;
  compliance: number;
  budget: number;
  threat: number;
  day: number;
  tickNumber: number;
  milestonesCompleted: number;
  milestonesTotal: number;
  objectivesCompleted: number;
  objectivesTotal: number;
  quizAttempted?: boolean;
  quizPassed?: boolean | null;
  rank?: number;
}

function inferMode(scenarioId: string | null | undefined): RunMode {
  if (!scenarioId) return 'sandbox';
  if (scenarioId.startsWith('tut_')) return 'training';
  return 'mission';
}

function isWinOutcome(outcome: LeaderboardOutcome): boolean {
  return outcome === 'won' || outcome === 'scenario_won';
}

function emptyStore(): PlayerAnalyticsStore {
  return { version: 1, runs: [], scenarioStats: {} };
}

function loadStore(): PlayerAnalyticsStore {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return emptyStore();
    const parsed = JSON.parse(raw) as PlayerAnalyticsStore;
    if (parsed?.version !== 1 || !Array.isArray(parsed.runs)) return emptyStore();
    return {
      version: 1,
      runs: parsed.runs,
      scenarioStats: parsed.scenarioStats ?? {},
    };
  } catch {
    return emptyStore();
  }
}

function saveStore(store: PlayerAnalyticsStore): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
}

export function recordRunCompletion(input: RecordRunInput): RunCompletionRecord {
  const store = loadStore();
  const scenarioId = input.scenarioId ?? null;
  const mode = inferMode(scenarioId);
  const score = calculateLeaderboardScore(input.compliance, input.budget, input.threat);

  const record: RunCompletionRecord = {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    date: new Date().toISOString(),
    scenarioId,
    scenarioTitle: input.scenarioTitle,
    mode,
    outcome: input.outcome,
    compliance: input.compliance,
    budget: input.budget,
    threat: input.threat,
    day: input.day,
    tickNumber: input.tickNumber,
    score,
    milestonesCompleted: input.milestonesCompleted,
    milestonesTotal: input.milestonesTotal,
    objectivesCompleted: input.objectivesCompleted,
    objectivesTotal: input.objectivesTotal,
    quizAttempted: input.quizAttempted ?? false,
    quizPassed: input.quizPassed ?? null,
    rank: input.rank,
  };

  store.runs = [record, ...store.runs].slice(0, MAX_RUN_HISTORY);

  const statKey = scenarioId ?? 'sandbox';
  const existing = store.scenarioStats[statKey] ?? {
    scenarioId: statKey,
    scenarioTitle: input.scenarioTitle,
    mode,
    attempts: 0,
    wins: 0,
    losses: 0,
    bestScore: 0,
  };

  existing.attempts += 1;
  if (isWinOutcome(input.outcome)) {
    existing.wins += 1;
    if (!existing.firstWin) existing.firstWin = record.date;
  } else {
    existing.losses += 1;
  }
  existing.bestScore = Math.max(existing.bestScore, score);
  existing.lastPlayed = record.date;
  existing.scenarioTitle = input.scenarioTitle;
  existing.mode = mode;
  store.scenarioStats[statKey] = existing;

  saveStore(store);
  return record;
}

export function updateLatestRunQuiz(passed: boolean): void {
  const store = loadStore();
  if (store.runs.length === 0) return;
  store.runs[0].quizAttempted = true;
  store.runs[0].quizPassed = passed;
  saveStore(store);
}

export function getTrainingProgress(): number {
  try {
    const raw = localStorage.getItem(TRAINING_PROGRESS_KEY);
    if (!raw) return 0;
    const parsed = parseInt(raw, 10);
    return Number.isFinite(parsed) ? parsed : 0;
  } catch {
    return 0;
  }
}

export function getRecentRuns(limit = 10): RunCompletionRecord[] {
  return loadStore().runs.slice(0, limit);
}

export function getScenarioStats(): ScenarioAggregate[] {
  return Object.values(loadStore().scenarioStats).sort((a, b) =>
    (b.lastPlayed ?? '').localeCompare(a.lastPlayed ?? ''),
  );
}

export function getCareerSummary(trainingTotal = 4): CareerSummary {
  const runs = loadStore().runs;
  const wins = runs.filter((r) => isWinOutcome(r.outcome)).length;
  const losses = runs.filter((r) => r.outcome === 'lost').length;
  const scores = runs.map((r) => r.score);
  const trainingProgress = getTrainingProgress();
  const wonScenarios = new Set(
    runs
      .filter((r) => isWinOutcome(r.outcome))
      .map((r) => r.scenarioId ?? 'sandbox'),
  );

  return {
    totalRuns: runs.length,
    totalWins: wins,
    totalLosses: losses,
    sandboxRuns: runs.filter((r) => r.mode === 'sandbox').length,
    trainingRuns: runs.filter((r) => r.mode === 'training').length,
    missionRuns: runs.filter((r) => r.mode === 'mission').length,
    trainingProgress,
    trainingTotal,
    uniqueScenariosPlayed: new Set(runs.map((r) => r.scenarioId ?? 'sandbox')).size,
    uniqueScenariosWon: wonScenarios.size,
    bestOverallScore: scores.length ? Math.max(...scores) : 0,
    avgScore: scores.length
      ? Math.round((scores.reduce((a, b) => a + b, 0) / scores.length) * 100) / 100
      : 0,
    quizAttempts: runs.filter((r) => r.quizAttempted).length,
    quizPasses: runs.filter((r) => r.quizPassed === true).length,
    winRate: runs.length ? Math.round((wins / runs.length) * 100) : 0,
    academyCompletionRate: trainingTotal
      ? Math.round((Math.min(trainingProgress, trainingTotal) / trainingTotal) * 100)
      : 0,
    lastPlayed: runs[0]?.date,
  };
}

export function buildCompletionReview(record: RunCompletionRecord): CompletionReview {
  const won = isWinOutcome(record.outcome);
  const objectiveRate =
    record.objectivesTotal > 0
      ? record.objectivesCompleted / record.objectivesTotal
      : won
        ? 1
        : 0;
  const milestoneRate =
    record.milestonesTotal > 0 ? record.milestonesCompleted / record.milestonesTotal : 1;

  let score = 0;
  if (won) score += 40;
  score += Math.round(objectiveRate * 30);
  score += Math.round(milestoneRate * 15);
  if (record.threat < 20) score += 10;
  else if (record.threat < 40) score += 5;
  if (record.compliance >= 80) score += 5;
  if (record.quizPassed === true) score += 5;
  if (record.quizPassed === false) score -= 5;

  const grade: CompletionGrade =
    score >= 90 ? 'A' : score >= 75 ? 'B' : score >= 60 ? 'C' : score >= 45 ? 'D' : 'F';

  const highlights: string[] = [];
  const improvements: string[] = [];

  if (won) {
    highlights.push(
      record.mode === 'training'
        ? 'Academy module objectives met — progress saved.'
        : record.mode === 'mission'
          ? 'Mission objectives secured under crisis pressure.'
          : 'Sandbox IPO achieved — empire stabilized.',
    );
  } else {
    improvements.push('Run ended in failure — retry after reviewing missed objectives.');
  }

  if (record.objectivesTotal > 0 && record.objectivesCompleted === record.objectivesTotal) {
    highlights.push(`All ${record.objectivesTotal} scenario objectives completed.`);
  } else if (record.objectivesTotal > 0) {
    improvements.push(
      `${record.objectivesTotal - record.objectivesCompleted} objective(s) incomplete at debrief.`,
    );
  }

  if (record.milestonesTotal > 0 && record.milestonesCompleted === record.milestonesTotal) {
    highlights.push(`Milestones cleared (${record.milestonesCompleted}/${record.milestonesTotal}).`);
  }

  if (record.compliance >= 80) {
    highlights.push(`Strong compliance posture (${Math.round(record.compliance)}%).`);
  } else if (record.compliance < 60) {
    improvements.push(`Compliance dipped to ${Math.round(record.compliance)}% — add governance buildings.`);
  }

  if (record.threat >= 40) {
    improvements.push(`Threat peaked at ${Math.round(record.threat)} — prioritize cyber controls.`);
  } else if (record.threat < 15) {
    highlights.push('Threat contained throughout the run.');
  }

  if (record.quizPassed === true) {
    highlights.push('Knowledge check passed — controls validated.');
  } else if (record.quizPassed === false) {
    improvements.push('Review the knowledge-check hint before your next attempt.');
  }

  const headline =
    grade === 'A' || grade === 'B'
      ? 'Professional readiness — strong debrief.'
      : grade === 'C'
        ? 'Adequate performance — room to optimize.'
        : 'Needs remediation — review gaps before next run.';

  return { grade, headline, highlights, improvements };
}

export function formatOutcomeLabel(outcome: LeaderboardOutcome): string {
  if (outcome === 'lost') return 'Failure';
  if (outcome === 'scenario_won') return 'Mission Win';
  return 'IPO Success';
}

export function formatModeLabel(mode: RunMode): string {
  if (mode === 'training') return 'Academy';
  if (mode === 'mission') return 'Scenario Ops';
  return 'Sandbox';
}

export function formatCareerExportLine(summary: CareerSummary): string {
  if (summary.totalRuns === 0) return '';
  return `Career record: ${summary.totalRuns} runs · ${summary.winRate}% win rate · best score ${summary.bestOverallScore.toFixed(1)} · academy ${summary.trainingProgress}/${summary.trainingTotal}`;
}